import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, FileText, Check, Phone, MessageCircle } from 'lucide-react';
import { getWhatsAppLink, WHATSAPP_NUMBER } from '../lib/utils';

const steps = [
  "Création du NIF",
  "Soumission de la demande en ligne",
  "Paiement des frais administratifs",
  "Contrôle et identification à l'ANR",
  "Prise de rendez-vous, données biométriques",
  "Suivi jusqu'à l'obtention"
];

const documents = [
  "Acte de naissance ou carte d'électeur",
  "Photos d'identité format passeport",
  "Ancien passeport (si renouvellement)"
];

export const PassportPage = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-[#020617] min-h-screen">
      {/* Header */}
      <section className="pt-6 pb-4 relative">
        <div className="absolute inset-0 gradient-radial opacity-30" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-[#94A3B8] hover:text-[#D4AF37] transition-colors mb-4" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
          </button>
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <h1 className="text-xl md:text-3xl font-bold text-white">Assistance Passeport</h1>
              <p className="text-[#94A3B8] text-xs">Pour les citoyens congolais</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-4">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div className="card-luxury p-4 bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/20 mb-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-amber-400 font-medium">Ce service est disponible uniquement pour les citoyens de la RDC.</p>
          </motion.div>

          <div className="max-w-xl mx-auto flex flex-col gap-6">
            {/* Price Card */}
            <motion.div className="card-luxury p-4" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-white">Assistance Passeport</h3>
                  <p className="text-[#94A3B8] text-xs">Accompagnement complet jusqu'à l'obtention</p>
                </div>
                <span className="text-3xl font-bold text-[#D4AF37]">180$</span>
              </div>
            </motion.div>

            {/* Steps */}
            <motion.div className="card-luxury p-4" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
              <h3 className="text-xs font-semibold text-white mb-3">Étapes de la Procédure</h3>
              <ol className="space-y-3">
                {steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] flex items-center justify-center font-semibold flex-shrink-0 text-xs">
                      {i + 1}
                    </div>
                    <p className="text-[#94A3B8] text-[13px] pt-0.5">{step}</p>
                  </li>
                ))}
              </ol>
            </motion.div>

            {/* Documents + Contact */}
            <div className="space-y-4">
              <motion.div className="card-luxury p-4" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <h3 className="text-sm font-semibold text-white mb-4">Documents Requis</h3>
                <ul className="space-y-3">
                  {documents.map((doc, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-[#94A3B8] text-xs">{doc}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              <motion.div className="card-luxury p-4 bg-gradient-to-br from-[#0F172A] to-[#1E293B]" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}>
                <h3 className="text-sm font-semibold text-white mb-4">Besoin d'Assistance?</h3>
                <p className="text-[#94A3B8] text-xs mb-4">Contactez-nous pour démarrer votre procédure</p>
                <div className="grid grid-cols-2 gap-4">
                  <a href="tel:+243975608422" className="flex flex-col items-center p-4 rounded-xl bg-white/5 border border-white/10 hover:border-[#D4AF37]/30 transition-colors" data-testid="call-btn">
                    <Phone className="w-8 h-8 text-[#D4AF37] mb-2" />
                    <span className="font-medium text-white text-sm">Appeler</span>
                  </a>
                  <a href={getWhatsAppLink(WHATSAPP_NUMBER, 'Bonjour, je souhaite une assistance passeport.')} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-4 rounded-xl bg-white/5 border border-white/10 hover:border-green-500/30 transition-colors" data-testid="whatsapp-btn">
                    <MessageCircle className="w-8 h-8 text-green-400 mb-2" />
                    <span className="font-medium text-white text-sm">WhatsApp</span>
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PassportPage;
